<template>
    <div class="w-full bg-gray-50 min-h-screen print:bg-white">
        <div class="space-y-2">
            <!-- Patient Header and Actions -->
            <div class="flex gap-4 items-start">
                <div class="flex-1">
                    <PatientHeader v-model:dataConsult="dataConsult" v-model:cnpPacient="cnpPacient"
                        v-model:selectedRecord="selectedRecord" v-model:lastNamePacient="lastNamePacient"
                        v-model:firstNamePacient="firstNamePacient" v-model:birthDatePacient="birthDatePacient"
                        v-model:sexPacient="sexPacient" v-model:phonePacient="phonePacient"
                        v-model:notesPacient="notesPacient" v-model:inaltimePacient="inaltimePacient"
                        v-model:greutatePacient="greutatePacient" v-model:bsaPacient="bsaPacient"
                        :previousRecords="previousRecords" @load="loadFormData" :disabled="examLocked" />
                </div>
                <div class="w-72 shrink-0 sticky top-4 self-start print:hidden">
                    <FormActionsBar :cnpPacient="cnpPacient" :lastNamePacient="lastNamePacient"
                        :firstNamePacient="firstNamePacient" :sexPacient="sexPacient" :examLocked="examLocked"
                        :hasSelectedRecord="!!currentExamId" @save="saveAndSetCurrentId" @exportPdf="exportPDF"
                        @finalize="openFinalizeConfirm" @reset="openResetConfirm" @delete="openDeleteConfirm" />
                </div>
            </div>
            <!-- Echo Form -->
            <EchoForm :sexPacient="sexPacient" v-model:DTDVS="DTDVS" v-model:DTDVSbsa="DTDVSbsa" v-model:DTSVS="DTSVS"
                v-model:DTSVSbsa="DTSVSbsa" v-model:SIVd="SIVd" v-model:PPVSd="PPVSd" v-model:MasaVS="MasaVS"
                v-model:RTW="RTW" v-model:FS="FS" v-model:FE="FE" v-model:FEvol="FEvol" v-model:MAPSE="MAPSE"
                v-model:VTDVS="VTDVS" v-model:VTDVSbsa="VTDVSbsa" v-model:VTSVS="VTSVS" v-model:VTSVSbsa="VTSVSbsa"
                v-model:DPmaxMitral="DPmaxMitral" v-model:DPmediuMitral="DPmediuMitral" v-model:VmaxMitral="VmaxMitral"
                v-model:SOMMitral="SOMMitral" v-model:PHTMitral="PHTMitral" v-model:SOM20Mitral="SOM20Mitral"
                v-model:dpdtMitral="dpdtMitral" v-model:concluzii="concluzii" :observatii="observatii"
                :getValidationClass="getValidationClass" @addObservation="addObservation" :disabled="examLocked" />
        </div>
        <ConfirmModal v-model:open="confirmOpen" :title="confirmTitle" :message="confirmMessage"
            :confirmText="confirmText" cancelText="Renunță" confirmVariant="danger" @confirm="handleConfirm" />
    </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";

import PatientHeader from "../patient/PatientHeader.vue";
import FormActionsBar from "../echo/FormActionsBar.vue";
import EchoForm from "../echo/EchoForm.vue";
import ConfirmModal from "../ui/ConfirmModal.vue";

import { useFormActions } from "../../composables/echo/useFormActions.js";
import { useEchoCalculations } from "../../composables/echo/useEchoCalculations.js";
import { useNotify } from "../../composables/useNotify.js";
import { useFormValidation } from "../../composables/echo/useFormValidation.js";
import { flattenExam } from "../../composables/echo/useExamHelpers.js";
import { observatii } from "../../../../src/constante.js";

import { selectedPatient, setSelectedPatient } from "../../stores/selectedPatient.js";

// for confirmation modal
const confirmOpen = ref(false);
const confirmTitle = ref("Confirmare");
const confirmMessage = ref("");
const confirmText = ref("Confirm");
const pendingAction = ref(null);

// Reactive data - Patient data
const form = {
    dataConsult: ref(new Date().toLocaleDateString("ro-RO").slice(0, 10)),
    cnpPacient: ref(""),
    lastNamePacient: ref(""),
    firstNamePacient: ref(""),
    birthDatePacient: ref(""),
    sexPacient: ref("F"),
    phonePacient: ref(""),
    notesPacient: ref(""),
    inaltimePacient: ref(""),
    greutatePacient: ref(""),
    bsaPacient: ref(""),
    selectedRecord: ref(null),

    // Reactive data - Echocardiography measurements
    DTDVS: ref(""),
    DTDVSbsa: ref(""),
    DTSVS: ref(""),
    DTSVSbsa: ref(""),
    SIVd: ref(""),
    PPVSd: ref(""),
    MasaVS: ref(""),
    RTW: ref(""),
    FS: ref(""),
    FE: ref(""),
    FEvol: ref(""),
    MAPSE: ref(""),
    VTDVS: ref(""),
    VTDVSbsa: ref(""),
    VTSVS: ref(""),
    VTSVSbsa: ref(""),

    // Mitral Valve
    DPmaxMitral: ref(""),
    DPmediuMitral: ref(""),
    VmaxMitral: ref(""),
    SOMMitral: ref(""),
    PHTMitral: ref(""),
    SOM20Mitral: ref(""),
    dpdtMitral: ref(""),

    // Conclusions
    concluzii: ref(""),

    currentExamId: ref(null),
    examStatus: ref("draft"),
    examLocked: ref(false)
}

const {
    dataConsult, cnpPacient, lastNamePacient, firstNamePacient, birthDatePacient, sexPacient,
    phonePacient, notesPacient, inaltimePacient, greutatePacient, bsaPacient, selectedRecord,
    DTDVS, DTDVSbsa, DTSVS, DTSVSbsa, SIVd, PPVSd, MasaVS, RTW, FS, FE, FEvol, MAPSE, VTDVS, VTDVSbsa, VTSVS, VTSVSbsa,
    DPmaxMitral, DPmediuMitral, VmaxMitral, SOMMitral, PHTMitral, SOM20Mitral, dpdtMitral, concluzii, currentExamId, examStatus, examLocked
} = form

const notify = useNotify();

// Initialize calculations
const { addObservation, birthDateDinCNP } = useEchoCalculations(form);
// Setup validation
const { getValidationClass } = useFormValidation(form);

// Initialize form actions
const { salvare, exportPDF, finalizeaza, resetare, usePatientRecords, stergeConsultatie } = useFormActions(form);

// Watch CNP changes to auto-fill birth date and sex
watch(cnpPacient, () => {
    if (cnpPacient.value && cnpPacient.value.length === 13) {
        birthDateDinCNP()
    }
})

// Real-time patient records using VueFire
const patientRecordsData = usePatientRecords(cnpPacient)

// Compute previous records for dropdown
const previousRecords = computed(() => {
    if (!patientRecordsData.value) return []

    return patientRecordsData.value
        .map((rec) => {
            const flattened = flattenExam(rec)
            return {
                date: flattened.dateConsult,
                id: flattened.id || rec.id,
                data: {
                    ...flattened,
                    id: flattened.id || rec.id,
                    status: rec.status || flattened.status || 'draft',
                    locked: rec.locked || false
                },
            }
        })
        .filter((x) => x.data.status !== 'void');
})

//helpers
const clearExamOnly = () => {
    selectedRecord.value = null;

    DTDVS.value = "";
    DTDVSbsa.value = "";
    DTSVS.value = "";
    DTSVSbsa.value = "";
    SIVd.value = "";
    PPVSd.value = "";
    MasaVS.value = "";
    RTW.value = "";
    FS.value = "";
    FE.value = "";
    FEvol.value = "";
    MAPSE.value = "";
    VTDVS.value = "";
    VTDVSbsa.value = "";
    VTSVS.value = "";
    VTSVSbsa.value = "";

    DPmaxMitral.value = "";
    DPmediuMitral.value = "";
    VmaxMitral.value = "";
    SOMMitral.value = "";
    PHTMitral.value = "";
    SOM20Mitral.value = "";
    dpdtMitral.value = "";

    concluzii.value = "";

    currentExamId.value = null;
    examStatus.value = "draft";
    examLocked.value = false;
};

const applyRecordToForm = (record) => {
    dataConsult.value = record.dataConsult || record.dateConsult || dataConsult.value;

    examStatus.value = record.status || "draft";
    examLocked.value = record.status === "final" || !!record.locked;
    currentExamId.value = record.id || null;

    DTDVS.value = record.DTDVS || "";
    DTDVSbsa.value = record.DTDVSbsa || "";
    DTSVS.value = record.DTSVS || "";
    DTSVSbsa.value = record.DTSVSbsa || "";
    SIVd.value = record.SIVd || "";
    PPVSd.value = record.PPVSd || "";
    MasaVS.value = record.MasaVS || "";
    RTW.value = record.RTW || "";
    FS.value = record.FS || "";
    FE.value = record.FE || "";
    FEvol.value = record.FEvol || "";
    MAPSE.value = record.MAPSE || "";
    VTDVS.value = record.VTDVS || "";
    VTDVSbsa.value = record.VTDVSbsa || "";
    VTSVS.value = record.VTSVS || "";
    VTSVSbsa.value = record.VTSVSbsa || "";

    DPmaxMitral.value = record.DPmaxMitral || "";
    DPmediuMitral.value = record.DPmediuMitral || "";
    VmaxMitral.value = record.VmaxMitral || "";
    SOMMitral.value = record.SOMMitral || "";
    PHTMitral.value = record.PHTMitral || "";
    SOM20Mitral.value = record.SOM20Mitral || "";
    dpdtMitral.value = record.dpdtMitral || "";

    concluzii.value = record.concluzii || record.conclusion || "";
}
// Methods
const loadFormData = () => {
    if (!selectedRecord.value) {
        notify.info("Selectați o înregistrare din listă", "Istoric");
        return;
    }

    applyRecordToForm(selectedRecord.value.data);
};

const saveAndSetCurrentId = async () => {
    const ok = await salvare();
    if (!ok) return;

    const docId = `${cnpPacient.value}_${(dataConsult.value || "").replace(/\./g, "-")}`;
    currentExamId.value = docId;

    // update store => lastRecord
    setSelectedPatient({
        ...(selectedPatient.value || {}),
        cnp: cnpPacient.value,
        cnpPacient: cnpPacient.value,
        lastName: lastNamePacient.value,
        firstName: firstNamePacient.value,
        sex: sexPacient.value,
        birthDate: birthDatePacient.value,
        phone: phonePacient.value,
        notes: notesPacient.value,
        height: inaltimePacient.value,
        weight: greutatePacient.value,
        bsa: bsaPacient.value,
        loadLast: true,
        lastRecord: {
            id: docId,
            status: examStatus.value || 'draft',
            locked: !!examLocked.value,
            dataConsult: dataConsult.value,
            DTDVS: DTDVS.value,
            DTDVSbsa: DTDVSbsa.value,
            DTSVS: DTSVS.value,
            DTSVSbsa: DTSVSbsa.value,
            SIVd: SIVd.value,
            PPVSd: PPVSd.value,
            MasaVS: MasaVS.value,
            RTW: RTW.value,
            FS: FS.value,
            FE: FE.value,
            FEvol: FEvol.value,
            MAPSE: MAPSE.value,
            VTDVS: VTDVS.value,
            VTDVSbsa: VTDVSbsa.value,
            VTSVS: VTSVS.value,
            VTSVSbsa: VTSVSbsa.value,
            DPmaxMitral: DPmaxMitral.value,
            DPmediuMitral: DPmediuMitral.value,
            VmaxMitral: VmaxMitral.value,
            SOMMitral: SOMMitral.value,
            PHTMitral: PHTMitral.value,
            SOM20Mitral: SOM20Mitral.value,
            dpdtMitral: dpdtMitral.value,
            concluzii: concluzii.value,
        },
    });
};

// confirmations
const openFinalizeConfirm = () => {
    if (!currentExamId.value) {
        notify.info("Salvează buletinul înainte să îl finalizezi.", "Finalizează");
        return;
    }
    if (examLocked.value) {
        notify.info("Buletinul este deja finalizat și blocat.", "Finalizează");
        return;
    }

    pendingAction.value = "finalize";
    confirmTitle.value = "Finalizează vizita";
    confirmMessage.value =
        "Finalizezi această vizită?\nDupă finalizare, nu vei mai putea modifica datele.";
    confirmText.value = "Finalizează";
    confirmOpen.value = true;
};

const openResetConfirm = () => {
    if (examLocked.value) {
        notify.info("Buletinul este blocat. Nu poate fi resetat.", "Resetare");
        return;
    }

    pendingAction.value = "reset";
    confirmTitle.value = "Resetare formular";
    confirmMessage.value = "Ești sigur(ă)? Această acțiune va reseta toate câmpurile!";
    confirmText.value = "Resetează";
    confirmOpen.value = true;
};

const handleConfirm = async () => {
    if (pendingAction.value === "finalize") {
        const examId = currentExamId.value;
        const success = await finalizeaza(examId);
        if (success) {
            examStatus.value = "final";
            examLocked.value = true;

            // update store lastRecord
            if (selectedPatient.value?.lastRecord?.id === examId) {
                setSelectedPatient({
                    ...selectedPatient.value,
                    loadLast: true,
                    lastRecord: {
                        ...selectedPatient.value.lastRecord,
                        status: "final",
                        locked: true,
                    },
                });
            }
        }
    } else if (pendingAction.value === "reset") {
        resetare();
    } else if (pendingAction.value === "delete") {
        const examId = selectedRecord.value?.id || currentExamId.value;
        const success = await stergeConsultatie(examId); // soft delete
        if (success) {
            clearExamOnly();

            if (selectedPatient.value?.lastRecord?.id === examId) {
                setSelectedPatient({
                    ...selectedPatient.value,
                    lastRecord: { ...selectedPatient.value.lastRecord, status: "final", locked: true },
                });
            }
        }
    }
    pendingAction.value = null;
    confirmOpen.value = false;
};

const openDeleteConfirm = () => {
    const examId = selectedRecord.value?.id || currentExamId.value;
    if (!examId) {
        notify.info("Selectează o vizită ca să o ștergi.", "Ștergere");
        return;
    }
    if (examLocked.value) {
        notify.info("Buletinul este blocat. Nu poate fi șters.", "Ștergere");
        return;
    }

    pendingAction.value = "delete";
    confirmTitle.value = "Șterge consultația";
    confirmMessage.value =
        "Ești sigur(ă) că vrei să ștergi această consultație?\nRecomandat: se va marca drept anulată (nu dispare complet).";
    confirmText.value = "Șterge";
    confirmOpen.value = true;
};

watch(
    () => selectedPatient.value,
    (p) => {
        if (!p) return;

        // set patient data in form
        const cnp = p.cnpPacient || p.cnp || "";
        if (cnp) cnpPacient.value = cnp;

        lastNamePacient.value = p.lastNamePacient || p.lastName || "";
        firstNamePacient.value = p.firstNamePacient || p.firstName || "";
        birthDatePacient.value = p.birthDatePacient || p.birthDate || "";
        sexPacient.value = p.sexPacient || p.sex || "F";
        phonePacient.value = p.phonePacient || p.phone || "";
        notesPacient.value = p.notesPacient || p.notes || "";
        inaltimePacient.value = p.inaltimePacient || p.height || "";
        greutatePacient.value = p.greutatePacient || p.weight || "";
        bsaPacient.value = p.bsaPacient || p.bsa || "";

        // if loadLast is set, load lastRecord into form
        if (p.loadLast && p.lastRecord) {
            // if voided record, just clear exam
            if (p.lastRecord.status === "void") {
                clearExamOnly();
                return;
            }
            applyRecordToForm(p.lastRecord);
            return;
        }

        // otherwise: new patient / only patient selected -> new draft exam
        clearExamOnly();
        dataConsult.value = new Date().toLocaleDateString("ro-RO").slice(0, 10);
    },
    { immediate: true }
);
</script>
